import React from 'react';
import { Container, MetricItem, MetricText, MetricValue, MetricDescription, LinesWrapper } from './styled';
import NumberOfAppartmentsIcon from 'assets/svg/hood/number-of-apartments-in-the-marketplace.svg';
import ElectionWinningPartyIcon from 'assets/svg/hood/election-winning-party.svg';
import DemographicIcon from 'assets/svg/hood/demographic-decile.svg';
import AVGRentPriceIcon from 'assets/svg/hood/avg-rent-price.svg';
import AVGPriceIcon from 'assets/svg/hood/avg-price.svg';
import SubwayLinesIcon from 'assets/svg/hood/subway_lines.svg';
import InfoIcon from 'assets/svg/info.svg';
import { useLocale } from 'locale';
import { IZoneSummaryProps } from 'utils/entities';
import Tooltip from 'ds/components/tooltip';
import { SmallText, TextLabel } from 'ds/components/typography';
import { TypeOfStatisticBlock } from 'components/widget/trends/chart/PriceStats';
import { TransportationAccordionLine } from 'store/state/selectors/insights/transportation';
import { LineWrapper } from 'components/widget/transportation/styled';
import { isNumber } from 'lodash';

type MetricsInsightsDataKey = TypeOfStatisticBlock | 'subwayLines' | 'schoolsScore';
type MetricsInsightsDataValue = number | string | TransportationAccordionLine[];
export type MetricsInsightsData = Partial<Record<MetricsInsightsDataKey, MetricsInsightsDataValue>>;

enum MetricType {
  AvgBuyPrice = 'avgBuyPrice',
  AvgRentPrice = 'avgRentPrice',
  BulletinsForSaleCount = 'bulletinsForSaleCount',
  PopularParty = 'popularParty',
  SchoolsRating = 'schoolsRating',
  DemographicIndex = 'demographicIndex',
  BulletinsForRentCount = 'bulletinsForRentCount',
  SubwayLines = 'subwayLines',
}

interface IconElement {
  key: MetricType;
  icon: React.ReactNode;
  value: number | string | TransportationAccordionLine[];
}

interface MetricProps {
  insightsData: MetricsInsightsData;
  summary: IZoneSummaryProps;
  type?: string;
}

const Metrics: React.FC<MetricProps> = ({ insightsData, summary, type = null }) => {
  const { t, formatMoney, formatNumber } = useLocale();

  const priceBuy = insightsData.buy;
  const priceRent = insightsData.rent;

  let map: IconElement[] = [
    {
      key: MetricType.AvgBuyPrice,
      icon: <AVGPriceIcon/>,
      value: priceBuy && isNumber(priceBuy) ? formatMoney(priceBuy) : null,
    },
    {
      key: MetricType.AvgRentPrice,
      icon: <AVGRentPriceIcon/>,
      value: priceRent && isNumber(priceRent) ? formatMoney(priceRent) : null,
    },
  ];


  if (summary) {
    map = [
      ...map,
      {
        key: MetricType.BulletinsForSaleCount,
        icon: <NumberOfAppartmentsIcon/>,
        value: summary.bulletinsForSaleCount ? formatNumber(summary.bulletinsForSaleCount) : null,
      },
      {
        key: MetricType.BulletinsForRentCount,
        icon: <NumberOfAppartmentsIcon/>,
        value: null,
      },
      {
        key: MetricType.PopularParty,
        icon: <ElectionWinningPartyIcon/>,
        value: summary.popularParty,
      },
      {
        key: MetricType.DemographicIndex,
        icon: <DemographicIcon/>,
        value: summary.demographicIndex,
      },
      {
        key: MetricType.SubwayLines,
        icon: <SubwayLinesIcon/>,
        value: insightsData.subwayLines,
      },
    ];
  }

  return (
    <Container data-auto="metrics">
      {
        map.map(({ icon, value, key }) => value ? (
          <MetricItem data-auto={`metric-${key}`} key={key}>
            {icon}
            <MetricText>
              <MetricValue weight="bold">
                {typeof value !== 'object' ? t('localPage.metrics.value', { key, value }) : (
                  <LinesWrapper>
                    {value.map((line, index) => (
                      <LineWrapper
                        key={`${line.label}${index}`}
                        color={line.color}
                        title={line.label}
                        shape="circle"
                        small
                      >
                        <TextLabel caps small>
                          {line.label.length > 2
                            ? line.label.slice(0, 2) + '..'
                            : line.label
                          }
                        </TextLabel>
                      </LineWrapper>
                    ))}
                  </LinesWrapper>
                )}
              </MetricValue>
              <MetricDescription>
                {type === 'city'
                  ? t('localPage.metrics.city', { key })
                  : t('localPage.metrics.neighbourhood', { key })}
                {key === MetricType.PopularParty ? (
                  <Tooltip
                    size="small"
                    placement="top"
                    usePowerTooltip
                    tooltip={<SmallText>{t('localPage.metrics.tooltip')}</SmallText>}
                    hideArrow
                  >
                    <InfoIcon />
                  </Tooltip>
                ) : null}
              </MetricDescription>
            </MetricText>
          </MetricItem>
        ) : null)}
    </Container>
  );
};

export default Metrics;
